import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import { Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { editorAccent } from 'modules/manager/ui/components/editor/editor-accent';
import { type GradientStop, joinStopColor, MIN_GRADIENT_STOPS, splitStopColor } from 'shared/fabric';
import { round } from 'shared/utils/round';
import UDCompactCheckbox from 'ud-form/components/compact-checkbox';
import UDIconButton from 'ud-ui/components/icon-button';
import { theme } from 'styles/theme/theme';
import ColorField from './color-field';
import CommitNumberField from './commit-number-field';
import InspectorRow from './inspector-row';
import InspectorSection from './inspector-section';
import type { FillGradientControl } from './use-fill-gradient';

interface FillGradientSectionProps {
  control: FillGradientControl;
}

/** `ColorField` пишет каждую букву; в точку уходит только законченный `#rgb` / `#rrggbb`. */
const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Доля 0–1 → проценты для поля; `+ 0` убирает `-0`, который модель читает у первой точки. */
const toPercent = (fraction: number): number => round(fraction * 100, 1) + 0;

const fromPercent = (percent: number): number => Math.min(Math.max(percent, 0), 100) / 100;

interface StopRowProps {
  stop: GradientStop;
  index: number;
  canRemove: boolean;
  control: FillGradientControl;
}

/**
 * One stop: hex color, opacity and position, as in Figma's gradient panel. Opacity is
 * a separate field because `ColorField` is an `<input type="color">` without alpha.
 * The hex text keeps a local draft so a half-typed `#ff` is not written into `fill`.
 */
const StopRow: React.FC<StopRowProps> = ({ stop, index, canRemove, control }) => {
  const { t } = useLingui();
  const number = index + 1;
  const { hex, alpha } = splitStopColor(stop.color);
  const [draft, setDraft] = useState(hex);

  useEffect(() => setDraft(hex), [hex]);

  const onColorChange = (value: string) => {
    setDraft(value);
    if (HEX_COLOR.test(value)) control.updateStop(index, { color: joinStopColor(value, alpha) });
  };

  return (
    <InspectorRow label={t`Точка ${number}`}>
      <Stop>
        <ColorField value={draft} onChange={onColorChange} />
        <StopNumbers>
          <NumberBox title={t`Положение, %`}>
            <CommitNumberField
              value={toPercent(stop.offset)}
              onCommit={(v) => control.updateStop(index, { offset: fromPercent(v) })}
              min={0}
              max={100}
              step={5}
              placeholder="%"
            />
          </NumberBox>
          <NumberBox title={t`Непрозрачность, %`}>
            <CommitNumberField
              value={toPercent(alpha)}
              onCommit={(v) => control.updateStop(index, { color: joinStopColor(hex, fromPercent(v)) })}
              min={0}
              max={100}
              step={5}
              placeholder="%"
            />
          </NumberBox>
          <UDIconButton
            variant="ghost"
            size="sm"
            icon={<Trash2 />}
            aria-label={t`Удалить точку`}
            tooltip={t`Удалить точку`}
            disabled={!canRemove}
            onClick={() => control.removeStop(index)}
          />
        </StopNumbers>
      </Stop>
    </InspectorRow>
  );
};

/**
 * "Gradient" inspector section: a linear gradient as a CSS angle and a list of stops —
 * the model the broadcast board editor uses (`shared/fabric`, IMGM-1622). Turning it on
 * seeds `defaultLinearGradient` of the current color (fade to transparent left to
 * right). Stops are editable only when every selected object has the same list.
 */
const FillGradientSection: React.FC<FillGradientSectionProps> = ({ control }) => {
  const { t } = useLingui();
  const on = control.enabled.value === true;
  const stops = control.stops.value;

  return (
    <InspectorSection
      title={t`Градиент`}
      headerAction={
        <GradientToggle checked={on} onChange={(e) => control.setEnabled(e.target.checked)} label={<Trans>Включить</Trans>} />
      }
    >
      {on && (
        <>
          <InspectorRow label={t`Угол, °`}>
            <CommitNumberField
              value={control.angle.isMixed || control.angle.value === undefined ? undefined : round(control.angle.value, 1)}
              onCommit={control.setAngle}
              step={15}
              placeholder={control.angle.isMixed ? t`(разное)` : undefined}
            />
          </InspectorRow>
          {control.stops.isMixed || !stops ? (
            <InspectorRow label={t`Точки`}>
              <MixedNote>
                <Trans>Точки у объектов разные</Trans>
              </MixedNote>
            </InspectorRow>
          ) : (
            <>
              <InspectorRow label={t`Точки`}>
                {/* Подписи столбцов стоят над полями точек: та же сетка, что у StopNumbers. */}
                <StopNumbers>
                  <ColumnCaption title={t`Положение, %`}>
                    <Trans>Позиция</Trans>
                  </ColumnCaption>
                  <ColumnCaption title={t`Непрозрачность, %`}>
                    <Trans>Непрозр.</Trans>
                  </ColumnCaption>
                  <UDIconButton
                    variant="ghost"
                    size="sm"
                    icon={<Plus />}
                    aria-label={t`Добавить точку`}
                    tooltip={t`Добавить точку`}
                    onClick={control.addStop}
                  />
                </StopNumbers>
              </InspectorRow>
              {stops.map((stop, index) => (
                <StopRow key={index} stop={stop} index={index} canRemove={stops.length > MIN_GRADIENT_STOPS} control={control} />
              ))}
            </>
          )}
        </>
      )}
    </InspectorSection>
  );
};

export default FillGradientSection;

const Stop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  min-width: 0;
`;

const StopNumbers = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const NumberBox = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const MixedNote = styled.span`
  color: ${theme.colors.Gray};
  font-size: 12px;
`;

const ColumnCaption = styled.span`
  flex: 1 1 0;
  min-width: 0;
  color: ${theme.colors.Gray};
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Переключатель в шапке секции — мельче и акцентнее, чем подпись строки инспектора.
const GradientToggle = styled(UDCompactCheckbox)`
  font-size: 11px;
  font-weight: 600;
  color: ${editorAccent.ink};
`;
